import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CompPagination, Container } from "components";
import { FaBan, FaEdit, FaTrash } from "react-icons/fa";
import Breadcrumb from "components/molecules/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { API_URL_banList } from "constants/api";
import { banListReducers } from "redux/banListSlice";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { deleteData, getData } from "actions";
import useDebounce from "hooks/useDebounce";
import { SyncLoader } from "react-spinners";
import moment from "moment";
import { AuthContext } from "context/AuthContext";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Email address", field: "email_address" },
  { title: "Last updated", field: "updated_at" },
  { title: "Actions", field: "" },
];

const Banlist = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);

  const {
    getBanListResult,
    getBanListLoading,
    getBanListError,
    addBanListResult,
    deleteBanListResult,
  } = useSelector((state) => state.banList);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_banList,
        params,
        { dispatch, redux: banListReducers },
        "GET_BANLIST"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_banList + item.id,
      { dispatch, redux: banListReducers },
      "DELETE_BANLIST"
    );
  };

  const onEdit = (item) => {
    navigate(`/administrator/email/banlists/edit`, {
      state: {
        item,
      },
    });
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [limit, offset, sortColumn, sortOrder]);

  useEffect(() => {
    if (addBanListResult) {
      fetchData();
    }
  }, [addBanListResult, fetchData]);

  useEffect(() => {
    if (deleteBanListResult) {
      fetchData();
    }
  }, [deleteBanListResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Ban email"}
        excludedPaths={["email", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          List of banned emails
          <div className="col-end-7 flex justify-end">
            <Link
              to="/administrator/email/banlists/create"
              className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <FaBan />
              Ban email
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by name"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getBanListLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getBanListError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getBanListError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getBanListResult.results &&
                  getBanListResult.results.length === 0 && (
                    <tr>
                      <td
                        className="text-center"
                        colSpan={TABLE_HEAD.length + 1}
                      >
                        <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                          No Data
                        </div>
                      </td>
                    </tr>
                  )}

                {getBanListResult.results &&
                  getBanListResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50 w-4/12">
                        <button
                          className="text-blue-700 dark:text-blue-300"
                          onClick={() => onEdit(item)}
                        >
                          {item.email_address}
                        </button>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-4/12">
                        {moment(item.updated_at).format("DD/MM/YYYY HH:mm:ss")}
                      </td>
                      <td className="p-4 border border-blue-gray-50">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={
                getBanListResult.count > 0 ? getBanListResult.count : 0
              }
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Banlist;
